<script setup>
import { onUnmounted, ref, computed, watch, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from 'vue-router'
import {
  Chart,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend)

const PRIMARY_COLOR = '#C23B7A'
const SECONDARY_COLOR = '#1B2A6B'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)

let refreshResultsTimer = setInterval(() => {
  resultsStore.fetchResults(route.params.eventAlias)
}, 30000)

// ── histogram controls ─────────────────────────────────────────────────────
const bucketSize = ref(10) // minutes
const isCumulative = ref(false)
const selectedHistogramMarker = ref(null)

// ── leaderboard category filter ────────────────────────────────────────────
const selectedCategory = ref('')

// ── finish-time histogram controls ────────────────────────────────────────
const finishBucketSize = ref(10) // minutes
const isFinishCumulative = ref(false)

// ── chart instances ────────────────────────────────────────────────────────
const histogramChart = ref(null)
const dropoutChart = ref(null)
const categoryChart = ref(null)
const finishTimeChart = ref(null)
const histogramCanvasRef = ref(null)
const dropoutCanvasRef = ref(null)
const categoryCanvasRef = ref(null)
const finishTimeCanvasRef = ref(null)

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function formatElapsed(ms) {
  if (!ms || ms <= 0) return '--'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

function markerLabel(text) {
  return (text || '').replace(/\|/g, ' ')
}

function elapsedMs(runner, splitTime) {
  if (!runner.start_time || !splitTime) return null
  const diff = new Date(splitTime) - new Date(runner.start_time)
  return diff > 0 ? diff : null
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 1 – Summary cards
// ═══════════════════════════════════════════════════════════════════════════

const registered = computed(() => resultsStore.results.filter(r => r.category_id !== 10).length)

const finished = computed(() =>
  resultsStore.results.filter(r => r.category_id !== 10 && (r.finish_time || r.finish_time_gps)).length
)

const inProgress = computed(() =>
  resultsStore.results.filter(r => {
    if (r.category_id === 10) return false
    if (r.finish_time || r.finish_time_gps) return false
    if (r.dnf || r.abs) return false
    return r.lastMarker !== null && r.lastMarker !== undefined
  }).length
)

const dnf = computed(() =>
  resultsStore.results.filter(r => {
    if (r.category_id === 10) return false
    if (r.finish_time || r.finish_time_gps) return false
    return r.dnf || r.abs || r.lastMarker === null || r.lastMarker === undefined
  }).length
)

const completionRate = computed(() => {
  if (registered.value === 0) return 0
  return ((finished.value / registered.value) * 100).toFixed(1)
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 2 – Checkpoint passage table
// ═══════════════════════════════════════════════════════════════════════════

const checkpointRows = computed(() => {
  const markers = Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
  const runners = resultsStore.results.filter(r => r.category_id !== 10)
  const total = runners.length

  return markers.map(marker => {
    const elapsed = []
    runners.forEach(runner => {
      const split = runner.splits?.[marker.id]
      if (split) {
        const ms = elapsedMs(runner, split.time)
        if (ms) elapsed.push(ms)
      }
    })

    const reached = elapsed.length
    const pct = total > 0 ? ((reached / total) * 100).toFixed(1) : '0.0'
    const avg = reached > 0 ? formatElapsed(elapsed.reduce((s, v) => s + v, 0) / reached) : '--'
    const fastest = reached > 0 ? formatElapsed(Math.min(...elapsed)) : '--'

    return { name: markerLabel(marker.text), reached, pct, avg, fastest }
  })
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 3 – Histogram
// ═══════════════════════════════════════════════════════════════════════════

const histogramMarkers = computed(() => {
  return Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
})

watch(histogramMarkers, (markers) => {
  if (markers.length && selectedHistogramMarker.value === null) {
    selectedHistogramMarker.value = markers[markers.length - 1]?.id ?? null
  }
}, { immediate: true })

function buildHistogramData(markerId, bktSize, cumulative) {
  const runners = resultsStore.results.filter(r => r.category_id !== 10)
  const elapsed = []

  runners.forEach(runner => {
    const split = runner.splits?.[markerId]
    const timeStr = split?.time
    const ms = timeStr ? elapsedMs(runner, timeStr) : null
    if (ms) elapsed.push(ms / 60000) // convert to minutes
  })

  if (elapsed.length === 0) return { labels: [], data: [] }

  const maxMin = Math.max(...elapsed)
  const numBuckets = Math.ceil(maxMin / bktSize) + 1
  const counts = new Array(numBuckets).fill(0)
  elapsed.forEach(m => {
    const idx = Math.floor(m / bktSize)
    if (idx < counts.length) counts[idx]++
  })

  const labels = counts.map((_, i) => `${i * bktSize}–${(i + 1) * bktSize} min`)

  let data
  if (cumulative) {
    data = counts.map((_, i) => counts.slice(0, i + 1).reduce((s, v) => s + v, 0))
  } else {
    data = counts
  }

  // trim trailing zeros
  let last = data.length - 1
  while (last > 0 && data[last] === 0) last--
  return { labels: labels.slice(0, last + 1), data: data.slice(0, last + 1) }
}

function renderHistogram() {
  if (!histogramCanvasRef.value || selectedHistogramMarker.value === null) return
  const { labels, data } = buildHistogramData(selectedHistogramMarker.value, bucketSize.value, isCumulative.value)

  if (histogramChart.value) {
    histogramChart.value.destroy()
    histogramChart.value = null
  }

  const marker = resultsStore.markersMap[selectedHistogramMarker.value]
  const title = marker ? markerLabel(marker.text) : ''

  histogramChart.value = new Chart(histogramCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: isCumulative.value ? `Cumulative arrivals – ${title}` : `Arrivals – ${title}`,
        data,
        backgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        borderWidth: 1,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => items[0].label,
            label: (item) => {
              const n = item.raw
              return isCumulative.value
                ? `${n} participants arrived up to this bucket`
                : `${n} participants arrived in this bucket`
            },
          },
        },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
      },
    },
  })
}

watch([selectedHistogramMarker, bucketSize, isCumulative, () => resultsStore.results.length], () => {
  nextTick(renderHistogram)
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 4 – Leaderboard
// ═══════════════════════════════════════════════════════════════════════════

const leaderboardCategories = computed(() => {
  const cats = new Set()
  resultsStore.results.forEach(r => {
    if (r.category_id && r.category_id !== 10) {
      const cat = resultsStore.categories.find(c => c.id === r.category_id)
      if (cat) cats.add(cat.name)
    }
  })
  return [...cats].sort()
})

const leaderboard = computed(() => {
  let runners = resultsStore.results.filter(r =>
    r.category_id !== 10 &&
    !r.dnf && !r.abs &&
    (r.finish_time || r.finish_time_gps) &&
    r.elapsedTime
  )

  if (selectedCategory.value) {
    runners = runners.filter(r => {
      const cat = resultsStore.categories.find(c => c.id === r.category_id)
      return cat && cat.name === selectedCategory.value
    })
  }

  return runners
    .sort((a, b) => a.elapsedTime - b.elapsedTime)
    .slice(0, 10)
    .map((r, i) => ({
      rank: i + 1,
      bib: r.number,
      name: r.name,
      category: resultsStore.categories.find(c => c.id === r.category_id)?.name || '',
      time: formatElapsed(r.elapsedTime),
    }))
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 5a – Dropout chart
// ═══════════════════════════════════════════════════════════════════════════

const dropoutData = computed(() => {
  const markers = Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
  const runners = resultsStore.results.filter(r => r.category_id !== 10)
  if (markers.length < 2) return { labels: [], data: [] }

  const labels = []
  const data = []

  for (let i = 0; i < markers.length - 1; i++) {
    const from = markers[i]
    const to = markers[i + 1]
    const reachedFrom = runners.filter(r => r.splits?.[from.id]).length
    const reachedTo = runners.filter(r => r.splits?.[to.id]).length
    const dropped = reachedFrom - reachedTo
    if (dropped >= 0) {
      labels.push(`${markerLabel(from.text)} → ${markerLabel(to.text)}`)
      data.push(dropped)
    }
  }

  return { labels, data }
})

function renderDropoutChart() {
  if (!dropoutCanvasRef.value) return
  const { labels, data } = dropoutData.value
  if (!labels.length) return

  if (dropoutChart.value) {
    dropoutChart.value.destroy()
    dropoutChart.value = null
  }

  dropoutChart.value = new Chart(dropoutCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Dropouts per segment',
        data,
        backgroundColor: SECONDARY_COLOR,
        borderColor: SECONDARY_COLOR,
        borderWidth: 1,
      }],
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { beginAtZero: true, ticks: { stepSize: 1 } },
      },
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 5b – Finish-time histogram
// ═══════════════════════════════════════════════════════════════════════════

function buildFinishTimeData(bktSize, cumulative) {
  const runners = resultsStore.results.filter(r =>
    r.category_id !== 10 && !r.dnf && !r.abs && r.elapsedTime > 0
  )
  if (runners.length === 0) return { labels: [], data: [] }

  const minutes = runners.map(r => r.elapsedTime / 60000)
  const maxMin = Math.max(...minutes)
  const numBuckets = Math.ceil(maxMin / bktSize) + 1
  const counts = new Array(numBuckets).fill(0)
  minutes.forEach(m => {
    const idx = Math.floor(m / bktSize)
    if (idx < counts.length) counts[idx]++
  })

  const labels = counts.map((_, i) => `${i * bktSize}–${(i + 1) * bktSize} min`)

  let data
  if (cumulative) {
    data = counts.map((_, i) => counts.slice(0, i + 1).reduce((s, v) => s + v, 0))
  } else {
    data = counts
  }

  // trim trailing zeros
  let last = data.length - 1
  while (last > 0 && data[last] === 0) last--
  return { labels: labels.slice(0, last + 1), data: data.slice(0, last + 1) }
}

function renderFinishTimeChart() {
  if (!finishTimeCanvasRef.value) return
  const { labels, data } = buildFinishTimeData(finishBucketSize.value, isFinishCumulative.value)
  if (labels.length === 0) return

  if (finishTimeChart.value) {
    finishTimeChart.value.destroy()
    finishTimeChart.value = null
  }

  finishTimeChart.value = new Chart(finishTimeCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: isFinishCumulative.value ? 'Narastająco – czas ukończenia' : 'Czas ukończenia',
        data,
        backgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        borderWidth: 1,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => items[0].label,
            label: (item) => {
              const n = item.raw
              return isFinishCumulative.value
                ? `${n} zawodników ukończyło do tego przedziału`
                : `${n} zawodników ukończyło w tym przedziale`
            },
          },
        },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
      },
    },
  })
}

watch([finishBucketSize, isFinishCumulative, () => resultsStore.results.length], () => {
  nextTick(renderFinishTimeChart)
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 5c – Category comparison
// ═══════════════════════════════════════════════════════════════════════════

const categoryComparisonData = computed(() => {
  if (leaderboardCategories.value.length < 2) return { labels: [], data: [] }

  const labels = []
  const data = []

  leaderboardCategories.value.forEach(catName => {
    const catObj = resultsStore.categories.find(c => c.name === catName)
    if (!catObj) return
    const finished = resultsStore.results.filter(r =>
      r.category_id === catObj.id && !r.dnf && !r.abs && r.elapsedTime
    )
    if (finished.length === 0) return
    const avg = finished.reduce((s, r) => s + r.elapsedTime, 0) / finished.length
    labels.push(catName)
    data.push(Math.round(avg / 60000)) // minutes
  })

  return { labels, data }
})

function renderCategoryChart() {
  if (!categoryCanvasRef.value) return
  const { labels, data } = categoryComparisonData.value
  if (labels.length < 2) return

  if (categoryChart.value) {
    categoryChart.value.destroy()
    categoryChart.value = null
  }

  categoryChart.value = new Chart(categoryCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Avg finish time (min)',
        data,
        backgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        borderWidth: 1,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } },
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// Lifecycle
// ═══════════════════════════════════════════════════════════════════════════

watch(() => resultsStore.results.length, () => {
  nextTick(() => {
    renderDropoutChart()
    renderFinishTimeChart()
    renderCategoryChart()
  })
}, { immediate: true })

onUnmounted(() => {
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
  histogramChart.value?.destroy()
  dropoutChart.value?.destroy()
  finishTimeChart.value?.destroy()
  categoryChart.value?.destroy()
})
</script>

<template>
  <div class="stats-page container-fluid py-3">
    <h1 class="mb-4">Statystyki</h1>

    <!-- ── Section 1: Summary cards ──────────────────────────────────────── -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card h-100 text-center shadow-sm">
          <div class="card-body">
            <div class="card-value">{{ registered }}</div>
            <div class="card-label">Zarejestrowani</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card h-100 text-center shadow-sm">
          <div class="card-body">
            <div class="card-value text-success">{{ finished }}</div>
            <div class="card-label">Ukończyli</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card h-100 text-center shadow-sm">
          <div class="card-body">
            <div class="card-value text-warning">{{ inProgress }}</div>
            <div class="card-label">W trakcie</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card h-100 text-center shadow-sm">
          <div class="card-body">
            <div class="card-value text-danger">{{ dnf }}</div>
            <div class="card-label">DNF / DNS</div>
          </div>
        </div>
      </div>
      <!-- Completion rate spans full width on mobile, 4 cols on desktop -->
      <div class="col-12 col-md-3">
        <div class="card h-100 text-center shadow-sm">
          <div class="card-body">
            <div class="card-value text-primary-brand">{{ completionRate }}%</div>
            <div class="card-label">Ukończalność</div>
            <div class="progress mt-2" style="height: 6px;">
              <div
                class="progress-bar progress-bar-brand"
                role="progressbar"
                :style="{ width: completionRate + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Section 2: Checkpoint passage table ───────────────────────────── -->
    <div class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Tabela punktów kontrolnych</div>
      <div class="table-responsive">
        <table class="table table-striped mb-0">
          <thead>
            <tr>
              <th>Punkt kontrolny</th>
              <th class="text-end">Osiągnęło</th>
              <th class="text-end">% startujących</th>
              <th class="text-end">Śr. czas od startu</th>
              <th class="text-end">Najszybszy</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in checkpointRows" :key="row.name">
              <td>{{ row.name }}</td>
              <td class="text-end">{{ row.reached }}</td>
              <td class="text-end">{{ row.pct }}%</td>
              <td class="text-end">{{ row.avg }}</td>
              <td class="text-end">{{ row.fastest }}</td>
            </tr>
            <tr v-if="checkpointRows.length === 0">
              <td colspan="5" class="text-center text-muted">Brak danych</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── Section 3: Arrival histograms ─────────────────────────────────── -->
    <div class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Histogram przybycia</div>
      <div class="card-body">
        <!-- Controls -->
        <div class="d-flex flex-wrap gap-3 mb-3 align-items-center">
          <div>
            <label class="form-label mb-0 me-2 small fw-semibold">Punkt:</label>
            <select
              v-model.number="selectedHistogramMarker"
              class="form-select form-select-sm d-inline-block"
              style="width: auto"
            >
              <option
                v-for="m in histogramMarkers"
                :key="m.id"
                :value="m.id"
              >{{ markerLabel(m.text) }}</option>
            </select>
          </div>
          <div>
            <label class="form-label mb-0 me-2 small fw-semibold">Przedział (min):</label>
            <select
              v-model.number="bucketSize"
              class="form-select form-select-sm d-inline-block"
              style="width: auto"
            >
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="15">15</option>
            </select>
          </div>
          <div class="form-check form-switch mb-0">
            <input
              id="cumulativeToggle"
              v-model="isCumulative"
              class="form-check-input"
              type="checkbox"
              role="switch"
            />
            <label class="form-check-label" for="cumulativeToggle">Narastająco</label>
          </div>
        </div>
        <!-- Chart canvas -->
        <div style="position: relative; height: 300px">
          <canvas ref="histogramCanvasRef"></canvas>
        </div>
      </div>
    </div>

    <!-- ── Section 4: Leaderboard ─────────────────────────────────────────── -->
    <div class="card shadow-sm mb-4">
      <div class="card-header fw-semibold d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>Top 10 zawodników</span>
        <select
          v-if="leaderboardCategories.length > 0"
          v-model="selectedCategory"
          class="form-select form-select-sm"
          style="width: auto"
        >
          <option value="">Wszystkie kategorie</option>
          <option v-for="cat in leaderboardCategories" :key="cat" :value="cat">{{ cat }}</option>
        </select>
      </div>
      <div class="table-responsive">
        <table class="table table-striped mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Numer</th>
              <th>Zawodnik</th>
              <th v-if="leaderboardCategories.length > 0">Kategoria</th>
              <th class="text-end">Czas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in leaderboard" :key="entry.bib">
              <td>{{ entry.rank }}</td>
              <td><span class="badge bg-secondary">{{ entry.bib }}</span></td>
              <td>{{ entry.name }}</td>
              <td v-if="leaderboardCategories.length > 0">{{ entry.category }}</td>
              <td class="text-end fw-semibold text-primary-brand">{{ entry.time }}</td>
            </tr>
            <tr v-if="leaderboard.length === 0">
              <td :colspan="leaderboardCategories.length > 0 ? 5 : 4" class="text-center text-muted">Brak ukończonych zawodników</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── Section 5a: Dropout chart ─────────────────────────────────────── -->
    <div v-if="dropoutData.labels.length >= 2" class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Odpadnięcia między punktami</div>
      <div class="card-body">
        <div :style="{ position: 'relative', height: Math.max(200, dropoutData.labels.length * 40) + 'px' }">
          <canvas ref="dropoutCanvasRef"></canvas>
        </div>
      </div>
    </div>

    <!-- ── Section 5b: Finish-time histogram ─────────────────────────────── -->
    <div v-if="finished > 0" class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Histogram czasów ukończenia</div>
      <div class="card-body">
        <!-- Controls -->
        <div class="d-flex flex-wrap gap-3 mb-3 align-items-center">
          <div>
            <label class="form-label mb-0 me-2 small fw-semibold">Przedział (min):</label>
            <select
              v-model.number="finishBucketSize"
              class="form-select form-select-sm d-inline-block"
              style="width: auto"
            >
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="15">15</option>
            </select>
          </div>
          <div class="form-check form-switch mb-0">
            <input
              id="finishCumulativeToggle"
              v-model="isFinishCumulative"
              class="form-check-input"
              type="checkbox"
              role="switch"
            />
            <label class="form-check-label" for="finishCumulativeToggle">Narastająco</label>
          </div>
        </div>
        <!-- Chart canvas -->
        <div style="position: relative; height: 300px">
          <canvas ref="finishTimeCanvasRef"></canvas>
        </div>
      </div>
    </div>

    <!-- ── Section 5c: Category comparison ───────────────────────────────── -->
    <div v-if="categoryComparisonData.labels.length >= 2" class="card shadow-sm mb-4">
      <div class="card-header fw-semibold">Porównanie kategorii – średni czas ukończenia</div>
      <div class="card-body">
        <div style="position: relative; height: 300px">
          <canvas ref="categoryCanvasRef"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stats-page {
  text-align: left;
}

.card-value {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.card-label {
  font-size: 0.85rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.text-primary-brand {
  color: #C23B7A;
}

.progress-bar-brand {
  background-color: #C23B7A;
}

@media (max-width: 375px) {
  .card-value {
    font-size: 1.8rem;
  }
}
</style>
